<script setup lang="ts">
import type PixInfoComponent from '@/components/withdraw/Info/Pix.vue';
import type AccountsMex from '@/components/user/account/BankAccounts/Mex.vue';
import type AstroPayComp from '@/components/withdraw/Info/AstroPay.vue';

const { isLoading } = storeToRefs(usePaymentsStore());

type Props = {
  userCountry: string;
  userCurrency: string;
  userLanguage: string;
  maxWithdrawAmount: number;
  minWithdrawAmount: number;
  userCurrentCreditAmount: number;
  withdrawMethod: PaymentsMethodsType;
  withdrawMethodIsPix: boolean;
  withdrawMethodIsSpei: boolean;
  withdrawMethodIsWithphone: boolean;
  initialAmount?: number;
};

const {
  userCountry,
  userCurrency,
  userLanguage,
  maxWithdrawAmount,
  minWithdrawAmount,
  userCurrentCreditAmount,
  withdrawMethod,
  withdrawMethodIsPix,
  withdrawMethodIsSpei,
  withdrawMethodIsWithphone,
  initialAmount,
} = defineProps<Props>();

type confirmRequestWithdrawTypes = {
  withdrawMethod: PaymentsMethodsType;
  withdrawAmount: number;
  selectedKeyType?: string;
  selectedKeyValue?: string;
  selectedBankAccountId?: number;
  pixKeyError?: string;
  selectedBankType?: string;
  selectedBankCode?: string;
  selectedBankNumber?: string;
  selectedPhoneNumber?: string;
  selectedPhoneDdi?: string;
};

type Emits = {
  'confirm-request-withdraw': [confirmRequestWithdrawTypes];
};

const withdrawMethodName = computed<string>(() => {
  return isLoading.value ? '-' : withdrawMethod;
});

const emit = defineEmits<Emits>();

const { t } = useTexts();

const { showMoneyFormat } = useWallet();

const pixComponentRef = ref<InstanceType<typeof PixInfoComponent> | null>(null);

const selectedKeyType = computed(() => {
  return pixComponentRef.value?.selectedKeyType ?? '';
});

const selectedKeyValue = computed(() => {
  return pixComponentRef.value?.selectedKeyValue ?? '';
});

const selectedBankAccountId = computed(() => {
  return pixComponentRef.value?.selectedBankAccountId;
});

const pixKeyError = computed(() => {
  return pixComponentRef.value?.pixKeyError ?? '';
});

const speiComponentRef = ref<InstanceType<typeof AccountsMex> | null>(null);

const selectedBankType = computed(() => {
  return speiComponentRef.value?.selectedBankType ?? '';
});

const selectedBankCode = computed(() => {
  return speiComponentRef.value?.selectedBankCode ?? '';
});

const selectedBankNumber = computed(() => {
  return speiComponentRef.value?.selectedBankNumber ?? '';
});

const astropayComponentRef = ref<InstanceType<typeof AstroPayComp> | null>(
  null,
);

const selectedPhoneNumber = computed(() => {
  return (
    pixComponentRef.value?.selectedPhoneNumber ??
    astropayComponentRef.value?.selectedPhoneNumber ??
    ''
  );
});

const selectedPhoneDdi = computed(() => {
  return (
    pixComponentRef.value?.selectedPhoneDdi ??
    astropayComponentRef.value?.selectedPhoneDdi ??
    ''
  );
});

const withdrawAmountValue = ref<number>(initialAmount || 0);

const errorMessage = ref<string>('');

const isWithdrawAmountValid = computed(() => {
  errorMessage.value = '';

  // valida se o campo não é 0,00
  if (!withdrawAmountValue.value || withdrawAmountValue.value === 0) {
    return false;
  }

  // valida valor mínimo de saque
  if (withdrawAmountValue.value < minWithdrawAmount) {
    errorMessage.value = `${t(
      'account.wallet.withdraw_modal.form_error_3',
    )} ${showMoneyFormat({ value: minWithdrawAmount / 100 })}`;

    return false;
  }

  // valida valor máximo de saque
  if (withdrawAmountValue.value > maxWithdrawAmount) {
    errorMessage.value = `${t(
      'account.wallet.withdraw_modal.form_error_5',
    )} ${showMoneyFormat({ value: maxWithdrawAmount / 100 })}`;

    return false;
  }

  // valida valor na carteira
  if (withdrawAmountValue.value > Math.round(userCurrentCreditAmount * 100)) {
    errorMessage.value = `${t(
      'account.wallet.withdraw_modal.form_error_4',
    )} ${showMoneyFormat({ value: userCurrentCreditAmount })}`;

    return false;
  }

  return true;
});

const isValid = computed<boolean>(() => {
  if (withdrawMethodIsSpei) {
    return (
      !!isWithdrawAmountValid.value &&
      (!!selectedBankType.value ||
        !!selectedBankCode.value ||
        !!selectedBankNumber.value)
    );
  }

  return (
    !!isWithdrawAmountValid.value &&
    (!!selectedBankAccountId.value ||
      (!!selectedKeyType.value && !!selectedKeyValue.value))
  );
});

const handdleRequestWithdraw = () => {
  if (!isValid.value) {
    return false;
  }

  emit('confirm-request-withdraw', {
    withdrawMethod,
    withdrawAmount: withdrawAmountValue.value,
    selectedKeyType: selectedKeyType.value,
    selectedKeyValue: selectedKeyValue.value,
    selectedBankAccountId: selectedBankAccountId.value,
    pixKeyError: pixKeyError.value,
    selectedBankType: selectedBankType.value,
    selectedBankCode: selectedBankCode.value,
    selectedBankNumber: selectedBankNumber.value,
    selectedPhoneNumber: selectedPhoneNumber.value,
    selectedPhoneDdi: selectedPhoneDdi.value,
  });
};
</script>

<template>
  <ClientOnly>
    <div class="step-form">
      <form
        class="step-form-item"
        @submit.prevent="handdleRequestWithdraw"
      >
        <div :class="[$style.depositRow, $style.withdrawInfosRow]">
          <div :class="$style.item">
            <label
              :class="$style.labelInput"
              for="method"
            >
              {{ $t('deposit.general.payment_method') }}
            </label>

            <BaseFormInput
              id="withdrawMethod"
              autocomplete="off"
              class="px-3"
              :compact="true"
              disabled
              :initial-value="withdrawMethodName"
              name="withdrawMethod"
              :readonly="true"
              type="text"
            />
          </div>
          <div :class="$style.item">
            <label
              :class="$style.labelInput"
              for="method"
            >
              {{ $t('account.wallet.withdraw_modal.min_withdraw_value') }}
            </label>

            <BaseFormInput
              id="minWithdrawAmount"
              autocomplete="off"
              class="px-3"
              :compact="true"
              disabled
              :initial-value="
                showMoneyFormat({
                  value: minWithdrawAmount / 100,
                  withCents: false,
                })
              "
              name="minWithdrawAmount"
              :readonly="true"
              type="text"
            />
          </div>
        </div>

        <div :class="$style.depositRow">
          <label
            :class="$style.labelMoney"
            for="inputCurrency"
          >
            {{ $t('account.wallet.withdraw_modal.withdraw_amount') }}
          </label>
          <BaseFormInputCurrency
            id="inputCurrency"
            v-model="withdrawAmountValue"
            :show-currency="true"
            size="lg"
            :user-country="userCountry"
            :user-currency="userCurrency"
            :user-language="userLanguage"
          />
        </div>

        <div :class="$style.avaliableAmount">
          {{ $t('account.wallet.withdraw_modal.withdraw_amount_available') }}
          <strong class="text-primary">
            {{ showMoneyFormat({ value: userCurrentCreditAmount }) }}
          </strong>
        </div>

        <div
          v-if="errorMessage && errorMessage !== ''"
          class="alert alert-error font-medium mt-2"
        >
          {{ errorMessage }}
        </div>

        <div :class="$style.infosMethod">
          <WithdrawInfoSpei
            v-if="withdrawMethodIsSpei"
            ref="speiComponentRef"
          />
          <WithdrawInfoAstroPay
            v-else-if="withdrawMethodIsWithphone"
            ref="astropayComponentRef"
          />
          <WithdrawInfoWorldline
            v-else-if="withdrawMethod === 'worldline'"
            :user-country="userCountry"
            :user-currency="userCurrency"
            :user-language="userLanguage"
          />
          <WithdrawInfoCrypto
            v-else-if="withdrawMethod === 'crypto'"
            :user-country="userCountry"
            :user-currency="userCurrency"
            :user-language="userLanguage"
          />
          <WithdrawInfoPix
            v-else-if="withdrawMethodIsPix"
            ref="pixComponentRef"
            :user-country="userCountry"
            :user-currency="userCurrency"
            :user-language="userLanguage"
          />
        </div>

        <button
          :class="$style.btnSubmit"
          :disabled="!isValid"
          tabindex="1"
          type="submit"
        >
          {{
            `${$t('account.wallet.withdraw')} 
            ${
              withdrawAmountValue > 0
                ? showMoneyFormat({ value: withdrawAmountValue / 100 })
                : ''
            }`
          }}
        </button>
      </form>
    </div>
  </ClientOnly>
</template>

<style module lang="postcss">
.depositRow {
  @apply flex flex-wrap gap-2 items-baseline justify-between;

  .labelMoney {
    @apply text-auth-texts text-xs font-semibold sm:text-sm;
  }

  &.withdrawInfosRow {
    @apply mb-4 flex-nowrap gap-4;

    .item {
      @apply flex-1;

      .labelInput {
        @apply text-auth-texts/50 font-medium text-xs;
      }

      input {
        @apply uppercase;
      }
    }
  }
}

.avaliableAmount {
  @apply flex gap-2 text-xs my-1;
}

.infosMethod {
  @apply border-solid border-auth-bg-inputs border-t border-b py-3 my-4 text-sm;
}

.btnSubmit {
  @apply flex justify-center items-center rounded-md font-medium text-lg uppercase bg-auth-button-deposit-bg text-auth-button-deposit-text w-full px-3 py-3 cursor-pointer;

  &[disabled]:not([disabled='false']) {
    @apply opacity-40 cursor-not-allowed;
  }
}
</style>

<style scoped lang="scss">
.step-form {
  .step-form-item {
    @apply text-texts text-sm;

    .item-key-button {
      @apply text-xs ml-2 inline-flex;
    }

    &-key {
      @apply flex items-center gap-x-2 text-lg flex-wrap gap-y-0 leading-6;

      svg {
        @apply text-primary;
      }
    }

    &-doc {
      @apply my-2 text-xs text-texts/80;
    }

    &-info {
      @apply text-warning text-xs;
    }

    .step-form-label {
      @apply flex gap-2 text-xs mb-2;
    }
    .step-form-input {
      @apply mb-2;
    }

    .btn-submit {
      @apply w-full bg-success text-texts font-medium mt-4;

      &[disabled]:not([disabled='false']) {
        @apply opacity-40 cursor-not-allowed;
      }
    }
  }
}
</style>
